import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import { contactStore, sectionStore, serviceStore } from '@/lib/variables'
import NisgCheck from '@/components/ui/NisgCheck'
import { useSectionHeading } from '@/lib/primarySection'
import './ServiceSection.css'

type Service = {
  id: string
  /** e.g. "New", shown next to the title */
  badge?: string
  /** a page with more, e.g. /nisg-2026 */
  link?: { href: string; label: string }
  title: string
  description: string
  modalContent?: {
    detailedDescription: string
    deliverables: string[]
  }
}

type Content = {
  eyebrow: string
  servicesTitle: string
  subtitle: string
  services: Service[]
}

export default function ServiceSection() {
  const { section, t, to } = useI18n()
  const c = section('services') as unknown as Content
  const services = Array.isArray(c.services) ? c.services : []
  const [open, setOpen] = useState<string | null>(null)

  // The announcement's "see the service" opens that entry.
  useEffect(() => serviceStore.on((id) => setOpen(id)), [])

  const capabilities = ['strategy', 'ux', 'devs', 'ai', 'delivery'].map((k) => t(`about.${k}`).replace('\n', ' '))

  // Fills the contact form with this service and scrolls to it.
  const ask = (s: Service) => {
    const nisg = s.id === 'cybersecurity'
    contactStore.prefill({
      subject: nisg ? t('inquiry.nisgSubject') : t('inquiry.subject').replace('{service}', s.title),
      message: nisg ? t('inquiry.nisgMessage') : t('inquiry.message').replace('{service}', s.title),
    })
    sectionStore.go('contact')
  }

  const { Tag: Title, className: titleClass } = useSectionHeading('about', 'h2')

  return (
    <div className="split">
      <div className="split__aside">
        <span className="eyebrow">{c.eyebrow}</span>
        <Title className={titleClass} style={{ marginTop: 12 }}>
          {c.servicesTitle}
        </Title>
        <p className="lede" style={{ marginTop: 20 }}>
          {c.subtitle}
        </p>
        <ul className="services__caps" aria-label="capabilities">
          {capabilities.map((cap, i) => (
            <li key={cap}>
              <span className="mono muted">0{i + 1}</span>
              {cap}
            </li>
          ))}
        </ul>
      </div>

      <ol className="services__list">
        {services.map((s, i) => {
          const isOpen = open === s.id
          const panelId = `service-panel-${s.id}`
          return (
            <li key={s.id} className={`services__row ${isOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="services__head"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? null : s.id)}
              >
                <span className="services__num mono">{String(i + 1).padStart(2, '0')}</span>
                <span className="services__title">
                  <h3>
                    {s.title}
                    {s.badge && <span className="services__badge mono">{s.badge}</span>}
                  </h3>
                  <span className="services__desc">{s.description}</span>
                </span>
                <span className="services__toggle" aria-hidden>
                  <span />
                  <span />
                </span>
              </button>
              {/* kept in the DOM so the height can animate (grid 0fr → 1fr); inert while closed */}
              <div id={panelId} className="services__panel" aria-hidden={!isOpen} inert={!isOpen}>
                <div className="services__panel-clip">
                <div className="services__panel-inner">
                  {s.modalContent?.detailedDescription && <p>{s.modalContent.detailedDescription}</p>}
                  {s.modalContent?.deliverables?.length ? (
                    <ul className="services__deliverables">
                      {s.modalContent.deliverables.map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  ) : null}
                  {s.id === 'cybersecurity' && <NisgCheck ask="scroll" showMore />}
                  <div className="services__actions">
                    <button type="button" className="btn btn--primary btn--sm" onClick={() => ask(s)}>
                      {t('inquiry.button')}
                      <span className="arrow" aria-hidden>
                        →
                      </span>
                    </button>
                    {s.link && (
                      <Link to={to(s.link.href)} className="services__more">
                        {s.link.label}
                      </Link>
                    )}
                  </div>
                </div>
                </div>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
